'use client';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { type FC, useMemo } from 'react';
import AnimatePng from '@/app/assets/imgs/style_animate.png';
import RealisticPng from '@/app/assets/imgs/style_realistic.png';
import { BaseFillIntelligentDuration } from '@/app/components/icons/BaseFillIntelligentDuration';
import { VipIcon } from '@/app/components/icons/VipIcon';
import { AspectRatioOption } from '@/app/components/shared/AspectRatioOption';
import { CommonSelect, type SelectOption } from '@/app/components/shared/CommonSelect';
import type { BadgeOptions } from '@/app/components/shared/CustomRadioGroup';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { FeatureTag } from '@/app/components/shared/FeatureTag';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { SettingRadioGroup } from '@/app/components/shared/SettingRadioGroup';
import { SettingsItem } from '@/app/components/shared/SettingsItem';
import { Switch } from '@/app/components/ui/switch';
import { MODEL_VERSION, SITE, TASK_TRANSITION_PRO_AD } from '@/app/constants';
import { type CodecOptionsResult, useCodecOptions, useDevice, useMembershipRevision, useQuota } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { FormTabType, OptionProps, OptionPropsWithType } from '@/app/types';
import { getAvailableOptions, isImageTask, isOptionAllowed } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import type { MovementAmplitudeType } from '../../../types';
import { ModelSelection } from './ModelSelection';

const STYLE_PREFERENCE_VIDEO_CHINA_SRC =
  'https://image01-aliyun.vidu.zone/vidu/media-asset/style_preference-1ddae8ff.mp4';
const STYLE_PREFERENCE_VIDEO_GLOBAL_SRC =
  'https://image01.cf.vidu.studio/vidu/media-asset/style_preference-1ddae8ff.mp4';

const CREATION_MODE_VIDEO_CHINA_SRC = 'https://image01-aliyun.vidu.zone/vidu/media-asset/q2-sample-527f276f.mp4';
const CREATION_MODE_VIDEO_GLOBAL_SRC = 'https://image01.cf.vidu.studio/vidu/media-asset/q2-sample-527f276f.mp4';

const stylePreferenceVideoSrc = SITE === 'china' ? STYLE_PREFERENCE_VIDEO_CHINA_SRC : STYLE_PREFERENCE_VIDEO_GLOBAL_SRC;
const creationModeVideoSrc = SITE === 'china' ? CREATION_MODE_VIDEO_CHINA_SRC : CREATION_MODE_VIDEO_GLOBAL_SRC;

/**
 * 模型选项组件
 * 用于选择视频生成的模型（如Vidu 2.0/Q1/Q2/Q2 Pro/Q3）
 *
 * @param value 当前选中的值
 * @param onChange 值变更回调
 */
export const ModelVersionOption: FC<{ type: Exclude<FormTabType, 'scene'> }> = ({ type }) => {
  const t = useTranslations();
  return (
    <SettingsItem title={t('create.createForm.settings.modelVersion.settingTitle')}>
      <ModelSelection type={type} />
    </SettingsItem>
  );
};
/**
 * 风格选项组件
 * 用于选择视频生成的风格（如写实/动漫）
 *
 * @param value 当前选中的值
 * @param onChange 值变更回调
 */
export const StyleOption: FC<OptionProps<string>> = ({ value, onChange }) => {
  const t = useTranslations();
  const options: SelectOption[] = [
    {
      value: 'general',
      label: t('create.createForm.settings.style.optionGeneral.text'),
      tooltip: <ImageWithPlaceholder src={RealisticPng.src} alt="bg" className="w-[128px] rounded-8" />,
    },
    {
      value: 'anime',
      label: t('create.createForm.settings.style.optionAnime.text'),
      tooltip: <ImageWithPlaceholder src={AnimatePng.src} alt="bg" className="w-[128px] rounded-8" />,
    },
  ];

  return (
    <SettingsItem title={t('create.createForm.settings.style.title')} info={t('create.createForm.settings.style.tip')}>
      <CommonSelect value={value || 'general'} options={options} onValueChange={onChange} />
    </SettingsItem>
  );
};

/**
 * 时长选项组件
 *
 * @param value 当前选中的值
 * @param onChange 值变更回调
 * @param onShouldUpgrade 用户需要升级时的回调
 */
export const DurationOption: FC<OptionPropsWithType<number>> = ({
  value,
  onChange,
  onShouldUpgrade,
  type,
  display = 'select',
}) => {
  const { userInfo, modelVersion, q3Character2videoTransition } = useStore((state) => {
    const mv = state[`${type}FormParams`].modelVersion;
    return {
      userInfo: state.userInfo,
      modelVersion: mv,
      q3Character2videoTransition:
        type === 'character2video' && mv === MODEL_VERSION.Q3
          ? state.character2videoFormParams.settingsQ3.transition
          : undefined,
    };
  });
  const t = useTranslations();

  const isFreeUser = userInfo?.subs_plan === 'free';

  const durationOptions =
    getAvailableOptions(modelVersion, type, {
      transition: q3Character2videoTransition,
    }).duration || [];

  // 处理 auto 选项：如果是 auto，显示 icon；否则显示数字
  const options: SelectOption[] = durationOptions.map((time) => {
    const isAuto = time === 'auto';
    const isNumeric = !isAuto && !Number.isNaN(Number(time));

    return {
      label: isAuto ? (
        <CustomTooltip content={t('create.createForm.settings.duration.option.auto')}>
          <BaseFillIntelligentDuration className="size-[14px] text-system-white" />
        </CustomTooltip>
      ) : display === 'segment' ? (
        time
      ) : (
        t('create.createForm.settings.duration.option.text', { time })
      ),
      value: time,
      badge: isFreeUser && isNumeric && Number(time) > 5 ? { content: <VipIcon className="mx-0.5 w-3" /> } : undefined,
    };
  });

  const selectOptions: SelectOption[] = options
    .filter((opt) => !opt.disabled)
    .map((option) => {
      return {
        value: option.value,
        label: option.label,
        badge: option.badge,
        isVipOption: !isOptionAllowed('duration', option.value, 'free', modelVersion),
      };
    });

  const handleChange = (val: string) => {
    // 检查用户订阅计划是否允许选择当前选项
    if (!isOptionAllowed('duration', val, userInfo?.subs_plan, modelVersion)) {
      onShouldUpgrade?.('duration');
      return;
    }
    onChange(val === 'auto' ? -1 : Number.parseInt(val, 10));
  };

  if (display === 'segment') {
    return (
      <SettingRadioGroup
        value={value === -1 || !value ? 'auto' : value.toString()}
        options={selectOptions}
        onValueChange={handleChange}
        labelClassName={modelVersion === MODEL_VERSION.Q3 ? 'font-normal' : undefined}
      />
    );
  }

  return (
    <CommonSelect
      value={value === -1 || !value ? 'auto' : value.toString()}
      options={selectOptions}
      onValueChange={handleChange}
    />
  );
};

/**
 * 清晰度选项组件
 * 用于选择视频的输出分辨率（极速/720p/1080p等）
 * 包含:
 * - 订阅计划限制逻辑
 * - 升级提示功能
 *
 * @param value 当前选中的值
 * @param onChange 值变更回调
 * @param onShouldUpgrade 用户需要升级时的回调
 */
export const ResolutionOption: FC<OptionPropsWithType<string>> = ({
  value,
  onChange,
  onShouldUpgrade,
  type,
  showVipIcon = true,
}) => {
  const t = useTranslations();
  const {
    remainingTrials,
    remainingTrialsQ1,
    remainingTrialsQ4_540p,
    remainingTrialsQ4_720p,
    remainingTrialsQ4_1080p,
    userInfo,
    modelVersion,
    q3Character2videoTransition,
  } = useStore((state) => {
    const mv = state[`${type}FormParams`].modelVersion;
    return {
      remainingTrials: state.trials720p?.remaining,
      remainingTrialsQ1: state.trialsQ1?.remaining,
      remainingTrialsQ4_540p: state.trialsQ4_540p?.remaining,
      remainingTrialsQ4_720p: state.trialsQ4_720p?.remaining,
      remainingTrialsQ4_1080p: state.trialsQ4_1080p?.remaining,
      userInfo: state.userInfo,
      modelVersion: mv,
      q3Character2videoTransition:
        type === 'character2video' && mv === MODEL_VERSION.Q3
          ? state.character2videoFormParams.settingsQ3.transition
          : undefined,
    };
  });
  const isFreeUser = userInfo?.subs_plan === 'free';

  const options: SelectOption[] = (
    getAvailableOptions(modelVersion, type, {
      transition: q3Character2videoTransition,
    }).resolution || []
  ).map((val) => {
    const label =
      val === '512'
        ? t('create.createForm.settings.resolution.option512.text')
        : val === '540p'
          ? '540p'
          : val === '720p'
            ? t('create.createForm.settings.resolution.option720p.text')
            : val === '1080p'
              ? t('create.createForm.settings.resolution.option1080p.text')
              : val.toUpperCase();

    // Q1 的免费试用显示到 1080p 这边
    const isQ1FreeFor1080p =
      modelVersion === MODEL_VERSION.Q1 &&
      val === '1080p' &&
      type !== 'reference2image' &&
      !!remainingTrialsQ1 &&
      remainingTrialsQ1 > 0;

    const is720pTrialApplicable =
      val === '720p' && isFreeUser && !!remainingTrials && !isOptionAllowed('resolution', val, 'free', modelVersion);

    const q4TrialRemainingByResolution = {
      '540p': remainingTrialsQ4_540p,
      '720p': remainingTrialsQ4_720p,
      '1080p': remainingTrialsQ4_1080p,
    } as const;
    const q4TrialRemaining =
      modelVersion === MODEL_VERSION.Q4
        ? q4TrialRemainingByResolution[val as keyof typeof q4TrialRemainingByResolution]
        : undefined;
    const isQ4TrialApplicable = typeof q4TrialRemaining === 'number' && q4TrialRemaining > 0;

    const tooltip: string | undefined = isQ4TrialApplicable
      ? t('create.createForm.settings.resolution.option1080p.tooltip', {
          count: q4TrialRemaining,
          s: q4TrialRemaining > 1 ? 's' : '',
          es: q4TrialRemaining > 1 ? 'es' : '',
        })
      : is720pTrialApplicable
        ? t('create.createForm.settings.resolution.option720p.tooltip', {
            count: remainingTrials,
            s: remainingTrials > 1 ? 's' : '',
            es: remainingTrials > 1 ? 'es' : '',
          })
        : isQ1FreeFor1080p
          ? t('create.createForm.settings.resolution.option1080p.tooltip', {
              count: remainingTrialsQ1,
              s: remainingTrialsQ1 > 1 ? 's' : '',
              es: remainingTrialsQ1 > 1 ? 'es' : '',
            })
          : undefined;

    let badge: BadgeOptions | undefined;
    if (isQ4TrialApplicable) {
      badge = {
        content: (
          <div className="ml-1 shrink-0 whitespace-nowrap rounded-4 bg-[rgba(76,99,255,0.48)] p-1 text-[10px] text-system-white leading-[1]">
            {t('create.createForm.settings.resolution.option1080p.remainingTrials', {
              count: q4TrialRemaining,
              s: q4TrialRemaining > 1 ? 's' : '',
              es: q4TrialRemaining > 1 ? 'es' : '',
            })}
          </div>
        ),
      };
    } else if (is720pTrialApplicable) {
      // 显示 720p 剩余试用次数
      badge = {
        content: (
          <div className="ml-1 shrink-0 whitespace-nowrap rounded-4 bg-[rgba(76,99,255,0.48)] p-1 text-[10px] text-system-white leading-[1]">
            {t('create.createForm.settings.resolution.option720p.remainingTrials', {
              count: remainingTrials,
              s: remainingTrials > 1 ? 's' : '',
              es: remainingTrials > 1 ? 'es' : '',
            })}
          </div>
        ),
      };
    } else if (val === '1080p' && isQ1FreeFor1080p) {
      // 显示 1080p 剩余免费试用次数
      badge = {
        content: (
          <div className="ml-1 shrink-0 whitespace-nowrap rounded-4 bg-[rgba(76,99,255,0.48)] p-1 text-[10px] text-system-white leading-[1]">
            {t('create.createForm.settings.resolution.option1080p.remainingTrials', {
              count: remainingTrialsQ1,
              s: remainingTrialsQ1 > 1 ? 's' : '',
              es: remainingTrialsQ1 > 1 ? 'es' : '',
            })}
          </div>
        ),
      };
    } else {
      const isVipOption = !isOptionAllowed('resolution', val, 'free', modelVersion);
      badge = { content: showVipIcon && isVipOption ? <VipIcon className="mx-0.5 w-3" /> : undefined };
    }

    return {
      value: val,
      label,
      tooltip,
      badge,
    };
  });

  return (
    <CommonSelect
      value={value || ''}
      options={options}
      onValueChange={(value) => {
        // 检查用户订阅计划是否允许选择当前选项
        if (!isOptionAllowed('resolution', value, userInfo?.subs_plan, modelVersion)) {
          if (value === '720p' && remainingTrials && remainingTrials > 0) {
            //仍有试用次数
            onChange(value);
            return;
          }
          onShouldUpgrade?.('resolution');
          return;
        }
        onChange(value);
      }}
    />
  );
};

export const DurationAndResolutionOption: FC<OptionPropsWithType<{ duration: number; resolution: string }>> = ({
  value,
  onChange,
  onShouldUpgrade,
  type,
}) => {
  const t = useTranslations();
  return (
    <SettingsItem
      title={
        <span>
          {t('create.createForm.settings.duration.title')}
          <span className="text-system-white/40"> & </span>
          {t('create.createForm.settings.resolution.title')}
        </span>
      }
      info={t('create.createForm.settings.durationAndResolution.tip')}
    >
      <div className="flex items-center gap-2">
        <DurationOption
          value={value?.duration}
          onChange={(val) => onChange({ duration: val, resolution: value?.resolution ?? '720p' })}
          onShouldUpgrade={onShouldUpgrade}
          type={type}
        />
        <ResolutionOption
          value={value?.resolution}
          onChange={(val) => onChange({ duration: value?.duration ?? 0, resolution: val })}
          onShouldUpgrade={onShouldUpgrade}
          type={type}
        />
      </div>
    </SettingsItem>
  );
};
/** 清晰度选项组件 */
export const ResolutionOptionSetting: FC<OptionPropsWithType<string>> = ({
  value,
  onChange,
  onShouldUpgrade,
  type,
}) => {
  const t = useTranslations();
  return (
    <SettingsItem title={<span>{t('create.createForm.settings.resolution.title')}</span>}>
      <div className="flex items-center gap-2">
        <ResolutionOption
          value={value}
          onChange={(val) => onChange(val)}
          onShouldUpgrade={onShouldUpgrade}
          type={type}
          showVipIcon={type !== 'reference2image'}
        />
      </div>
    </SettingsItem>
  );
};

/**
 * 运动幅度选项组件
 *
 * @param value 当前选中的值
 * @param onChange 值变更回调
 */
export const MovementOption: FC<OptionProps<MovementAmplitudeType>> = ({ value, onChange }) => {
  const t = useTranslations();
  const pathname = usePathname();
  const formType = pathname.split('/').pop();
  const formParams = useStore((state) => state[`${formType as FormTabType}FormParams`]);
  const disabled = formParams && 'isProMode' in formParams && formParams.isProMode;
  const disabledTip = disabled ? t('create.createForm.settings.movementAmplitude.tipProMode') : '';
  const options: SelectOption[] = [
    {
      value: 'auto',
      label: t('create.createForm.settings.movementAmplitude.optionAuto.text'),
    },
    {
      value: 'small',
      label: t('create.createForm.settings.movementAmplitude.optionSmall.text'),
    },
    {
      value: 'medium',
      label: t('create.createForm.settings.movementAmplitude.optionMedium.text'),
    },
    {
      value: 'large',
      label: t('create.createForm.settings.movementAmplitude.optionLarge.text'),
    },
  ];

  const selectOptions: SelectOption[] = options.map((option) => ({
    value: option.value,
    label: option.label,
  }));

  return (
    <CommonSelect
      value={value || ''}
      options={selectOptions}
      onValueChange={(value) => onChange(value as MovementAmplitudeType)}
      disabled={disabled}
      disabledTip={disabledTip}
    />
  );
};

/**
 * 模型类型选项组件
 *
 * @param value 当前选中的值
 * @param onChange 值变更回调
 */
export const CreationModeOption: FC<OptionPropsWithType<string>> = ({ value, onChange, onShouldUpgrade, type }) => {
  const t = useTranslations();
  const {
    remainingTrialsQ2TransitionSpeed = 0,
    remainingTrialsQ2TransitionPro = 0,
    userInfo,
    imagePrompts,
    modelVersion,
  } = useStore((state) => ({
    remainingTrialsQ2TransitionSpeed: state.trialsQ2TransitionSpeed?.remaining,
    remainingTrialsQ2TransitionPro: state.trialsQ2TransitionPro?.remaining,
    userInfo: state.userInfo,
    imagePrompts: state[`${type}FormParams`].imagePrompts,
    modelVersion: state[`${type}FormParams`].modelVersion,
  }));

  const validImageCount = useMemo(() => {
    return imagePrompts?.filter((p) => p !== null).length ?? 0;
  }, [imagePrompts]);

  // 只有 Q2 模型版本且非智能多帧模式才可使用 Q2 的试用次数
  const isQ2TransitionTrial = modelVersion === MODEL_VERSION.Q2 && validImageCount <= 2;

  const isProVipOption = !isOptionAllowed('transition', 'pro', 'free', modelVersion);

  const renderRemainingTrialsBadge = (remainingTrials: number, isVipFeature = false): BadgeOptions | undefined => {
    return remainingTrials > 0
      ? {
          content: (
            <>
              {isVipFeature && <VipIcon className="mx-0.5 w-3" />}
              <div className="ml-1 rounded-4 bg-[rgba(76,99,255,0.48)] p-1 text-[10px] text-system-white leading-[1]">
                {t('create.createForm.settings.freeTrials', {
                  count: remainingTrials,
                  s: remainingTrials > 1 ? 's' : '',
                  es: remainingTrials > 1 ? 'es' : '',
                })}
              </div>
            </>
          ),
        }
      : isVipFeature
        ? { content: <VipIcon className="mx-0.5 w-3" /> }
        : undefined;
  };

  const baseOptions: SelectOption[] = [
    {
      value: 'speed',
      label: t('create.createForm.settings.creationMode.optionFlash.text'),
      badge: renderRemainingTrialsBadge(isQ2TransitionTrial ? remainingTrialsQ2TransitionSpeed : 0),
    },
    {
      value: 'pro',
      label: t('create.createForm.settings.creationMode.optionCinematic.text'),
      badge: renderRemainingTrialsBadge(isQ2TransitionTrial ? remainingTrialsQ2TransitionPro : 0, isProVipOption),
    },
  ];

  const options: SelectOption[] =
    type === 'character2video' && modelVersion === MODEL_VERSION.Q3
      ? [
          ...baseOptions,
          {
            value: TASK_TRANSITION_PRO_AD,
            label: t('create.createForm.settings.creationMode.optionProAd.text'),
            badge: {
              content: <FeatureTag className="ml-1" />,
            },
          },
        ]
      : baseOptions;

  return (
    <CommonSelect
      value={value || ''}
      options={options}
      onValueChange={(value) => {
        if (!isOptionAllowed('transition', value, userInfo?.subs_plan, modelVersion)) {
          // 免费用户有试用次数时，可以试用 pro 模式
          if (value === 'pro' && isQ2TransitionTrial && remainingTrialsQ2TransitionPro > 0) {
            onChange(value);
            return;
          }
          onShouldUpgrade?.('transition');
          return;
        }
        onChange(value);
      }}
    />
  );
};

/**
 * 运动幅度选项组件
 *
 * @param value 当前选中的值
 * @param onChange 值变更回调
 */
export const MovementAmplitude: FC<OptionProps<MovementAmplitudeType>> = ({ value, onChange, onShouldUpgrade }) => {
  const t = useTranslations();
  return (
    <SettingsItem title={`${t('create.createForm.settings.movementAmplitude.title')}`}>
      <MovementOption value={value} onChange={onChange} onShouldUpgrade={onShouldUpgrade} />
    </SettingsItem>
  );
};
export const MovementAmplitudeAndAspectRatioOption: FC<
  OptionPropsWithType<{ movementAmplitude: MovementAmplitudeType; aspectRatio: string }>
> = ({ value, onChange, onShouldUpgrade, type }) => {
  const t = useTranslations();
  const modelVersion = useStore((state) => state[`${type}FormParams`].modelVersion);
  return (
    <SettingsItem
      title={
        <span>
          {t('create.createForm.settings.movementAmplitude.title')}
          <span className="text-system-white/40"> & </span>
          {t('create.createForm.settings.aspectRatio.title')}
        </span>
      }
    >
      <div className="flex items-center gap-2">
        <MovementOption
          value={value?.movementAmplitude}
          onChange={(val) => onChange({ movementAmplitude: val, aspectRatio: value?.aspectRatio ?? '16:9' })}
          onShouldUpgrade={onShouldUpgrade}
        />
        <AspectRatioOption
          value={value?.aspectRatio}
          onChange={(val) => onChange({ movementAmplitude: value?.movementAmplitude ?? 'auto', aspectRatio: val })}
          type={type}
          modelVersion={modelVersion}
        />
      </div>
    </SettingsItem>
  );
};
/** 宽高比选项组件 */
export const AspectRatioOptionSetting: FC<OptionPropsWithType<string>> = ({ value, onChange, type }) => {
  const t = useTranslations();
  const modelVersion = useStore((state) => state[`${type}FormParams`].modelVersion);
  return (
    <SettingsItem title={<span>{t('create.createForm.settings.aspectRatio.title')}</span>}>
      <div className="flex items-center gap-2">
        <AspectRatioOption value={value} onChange={onChange} type={type} modelVersion={modelVersion} />
      </div>
    </SettingsItem>
  );
};
/**
 * 生成数量选项组件
 * 控制单次任务生成的视频数量
 * 包含订阅计划限制和升级提示功能
 *
 * @param value 当前选中的数量值
 * @param onChange 值变更回调
 * @param onOpenPlanDetailDialog 打开计划详情对话框的回调
 * @param type 表单类型
 */
export const AmountOption: FC<OptionPropsWithType<number>> = ({ value, onChange, onShouldUpgrade, type }) => {
  const t = useTranslations();

  const userInfo = useStore((state) => state.userInfo);
  const modelVersion = useStore((state) => state[`${type}FormParams`].modelVersion);
  const isFreeUser = userInfo?.subs_plan === 'free';
  const isVipOption = !isImageTask({ type });

  const options: SelectOption[] = [
    {
      value: '1',
      label: '1',
    },
    {
      value: '2',
      label: '2',
      isVipOption,
    },
    {
      value: '3',
      label: '3',
      isVipOption,
    },
    {
      value: '4',
      label: '4',
      isVipOption,
    },
  ];

  return (
    <SettingsItem title={t('create.createForm.settings.amount.title')}>
      <CustomTooltip
        side="right"
        sideOffset={4}
        trigger="auto"
        contentClassName="max-w-[320px] p-0 sm:max-w-[320px] overflow-visible"
        content={
          isVipOption && isFreeUser ? (
            <div className="relative flex items-center pr-4 pl-1">
              <img
                src="https://image01.cf.vidu.studio/vidu/activity/amount-upgradeTip-img.d2412e4f.png"
                alt="amount-upgradeTip-img"
                className="relative top-[-5px] h-[58px] w-[86px]"
              />
              <div className="inline-block px-1 py-[10px] font-semibold">
                {t.rich('create.createForm.settings.amount.upgradeTip', {
                  upgrade: (chunks) => (
                    <span
                      className="inline-block cursor-pointer bg-[linear-gradient(90deg,#5AD0FF_0%,#009BFF_19.78%)] bg-clip-text text-transparent"
                      onClick={() => onShouldUpgrade?.('amount')}
                    >
                      {chunks}
                    </span>
                  ),
                })}
              </div>
            </div>
          ) : undefined
        }
      >
        <SettingRadioGroup
          value={(value || 1).toString()}
          options={options}
          onValueChange={(val) => {
            if (isVipOption && !isOptionAllowed('sample_count', val, userInfo?.subs_plan, modelVersion)) {
              onShouldUpgrade?.('amount');
              return;
            }
            onChange(Number.parseInt(val, 10));
          }}
        />
      </CustomTooltip>
    </SettingsItem>
  );
};

/**
 * 模型类型选项组件
 *
 * @param value 当前选中的值
 * @param onChange 值变更回调
 */
export const CreationModeOptionSetting: FC<OptionPropsWithType<string>> = ({
  value,
  onChange,
  type,
  onShouldUpgrade,
}) => {
  const t = useTranslations();
  return (
    <SettingsItem
      title={`${t('create.createForm.settings.creationMode.title')}`}
      tooltipContentClassName="max-w-[450px]"
      info={
        <div className="relative w-full">
          <video src={creationModeVideoSrc} autoPlay muted loop className="w-full rounded-lg" />
          <div className="absolute bottom-0 left-0 flex w-full">
            <div className="flex w-1/2 flex-col items-start justify-end gap-1 p-3">
              <div className="rounded-lg bg-black/60 p-2 font-semibold text-sm text-white leading-[1]">
                {t('create.createForm.settings.creationMode.optionFlash.text')}
              </div>
              <div className="text-white/80 text-xs opacity-70">
                {t('create.createForm.settings.creationMode.optionFlash.tooltip')}
              </div>
            </div>
            <div className="flex w-1/2 flex-col items-start justify-end gap-1 p-3">
              <div className="rounded-lg bg-black/60 p-2 font-semibold text-sm text-white leading-[1]">
                {t('create.createForm.settings.creationMode.optionCinematic.text')}
              </div>
              <div className="text-white/80 text-xs opacity-70">
                {t('create.createForm.settings.creationMode.optionCinematic.tooltip')}
              </div>
            </div>
          </div>
        </div>
      }
    >
      <CreationModeOption value={value} onChange={onChange} type={type} onShouldUpgrade={onShouldUpgrade} />
    </SettingsItem>
  );
};

export const DurationOptionSetting: FC<OptionPropsWithType<number>> = ({
  value,
  onChange,
  type,
  display,
  onShouldUpgrade,
}) => {
  const t = useTranslations();
  return (
    <SettingsItem title={`${t('create.createForm.settings.duration.title')}`} itemClassName="overflow-x-hidden">
      <DurationOption
        value={value}
        onChange={onChange}
        type={type}
        display={display}
        onShouldUpgrade={onShouldUpgrade}
      />
    </SettingsItem>
  );
};

/**
 * 错峰模式选项组件
 * 活动前保留旗舰版及企业版权限，活动后开放所有会员，额度沿用现有接口和提交校验
 *
 * @param value 当前开关状态
 * @param onChange 值变更回调
 * @param onOpenPlanDetailDialog 打开计划详情对话框的回调
 */
export const NonPeakModeOption: FC<OptionProps<boolean>> = ({ value, onChange, onOpenPlanDetailDialog }) => {
  const t = useTranslations();
  const { isMobile } = useDevice();
  const locale = useLocale();
  const isNewMembership = useMembershipRevision();
  const canUseNopeak = useStore(
    (state) =>
      Boolean(state.userInfo) &&
      isOptionAllowed('schedule_mode', 'nopeak', state.userInfo?.subs_plan, undefined, isNewMembership),
  );
  const userQuota = useStore((state) => state.userQuota);
  const { nopeakDailyLimit } = useQuota();
  const upgradeTipKey = isNewMembership
    ? 'create.createForm.settings.nonPeakMode.upgradeMembershipTip'
    : 'create.createForm.settings.nonPeakMode.upgradeTip';

  return (
    <SettingsItem
      title={
        <div className="flex items-center">
          {t('create.createForm.settings.nonPeakMode.title')}
          {!isNewMembership && <VipIcon className="ml-0.5 w-3" />}
        </div>
      }
      info={
        canUseNopeak
          ? t(
              isNewMembership
                ? 'create.createForm.settings.nonPeakMode.tipMember'
                : 'create.createForm.settings.nonPeakMode.tipUltimate',
              {
                limit: isNewMembership ? (userQuota?.nopeak_quota?.total ?? 0) : nopeakDailyLimit,
              },
            )
          : t('create.createForm.settings.nonPeakMode.tip')
      }
    >
      <div className="flex h-full items-center justify-end gap-2 md:justify-start">
        {canUseNopeak && userQuota?.nopeak_quota && (
          <div className="text-sm text-system-white/40">
            {t('create.createForm.settings.nonPeakMode.usage')}{' '}
            <span className="text-system-white">{userQuota.nopeak_quota.used}</span> /{userQuota.nopeak_quota.total}
          </div>
        )}
        <Switch
          className="h-7 w-12 p-1"
          checked={value}
          onCheckedChange={(checked) => {
            if (!canUseNopeak) {
              onOpenPlanDetailDialog?.();
            } else {
              onChange(checked);
            }
          }}
        />
        {!canUseNopeak &&
          (isMobile ? (
            <span className="text-sm text-system-white64" onClick={onOpenPlanDetailDialog}>
              {t.rich(upgradeTipKey, {
                upgrade: (chunks) => <span className="cursor-pointer text-system-blue02">{chunks}</span>,
              })}
            </span>
          ) : (
            <CustomTooltip
              side="right"
              content={
                <img
                  src={
                    locale === 'zh'
                      ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/nonpeakmode-d37394ee.webp'
                      : 'https://image01.cf.vidu.studio/vidu/activity/stagger-tips-img.b0544390.png'
                  }
                  alt="stagger-tips-img"
                  className="h-[124px] w-[226px]"
                />
              }
              disableHoverableContent={true}
              contentClassName="w-[250px] h-[148px] p-3"
            >
              <span className="cursor-pointer text-sm text-system-white64" onClick={onOpenPlanDetailDialog}>
                {t.rich(upgradeTipKey, {
                  upgrade: (chunks) => <span className="cursor-pointer text-system-blue02">{chunks}</span>,
                })}
              </span>
            </CustomTooltip>
          ))}
      </div>
    </SettingsItem>
  );
};

/**
 * 风格偏好选项组件
 * 用于选择视频生成的风格偏好（创意/稳定）
 *
 * @param value 当前选中的值
 * @param onChange 值变更回调
 */
export const StylePreferenceOption: FC<OptionProps<string>> = ({ value, onChange }) => {
  const t = useTranslations();

  const options: SelectOption[] = [
    {
      value: 'creative',
      label: t('create.createForm.settings.stylePreference.optionCreative.text'),
    },
    {
      value: 'stable',
      label: t('create.createForm.settings.stylePreference.optionStable.text'),
    },
  ];

  return (
    <SettingsItem
      title={t('create.createForm.settings.stylePreference.title')}
      info={
        <div className="relative w-full max-w-[400px]">
          <video src={stylePreferenceVideoSrc} autoPlay muted loop className="w-full rounded-lg" />
          <div className="absolute bottom-0 left-0 flex w-full">
            <div className="flex w-1/2 flex-col items-start justify-end gap-1 p-3">
              <div className="rounded-lg bg-black/60 p-2 font-semibold text-sm text-white leading-[1]">
                {t('create.createForm.settings.stylePreference.optionCreative.text')}
              </div>
              <div className="text-white/80 text-xs opacity-70">
                {t('create.createForm.settings.stylePreference.optionCreative.tooltip')}
              </div>
            </div>
            <div className="flex w-1/2 flex-col items-start justify-end gap-1 p-3">
              <div className="rounded-lg bg-black/60 p-2 font-semibold text-sm text-white leading-[1]">
                {t('create.createForm.settings.stylePreference.optionStable.text')}
              </div>
              <div className="text-white/80 text-xs opacity-70">
                {t('create.createForm.settings.stylePreference.optionStable.tooltip')}
              </div>
            </div>
          </div>
        </div>
      }
    >
      <CommonSelect
        value={value || ''}
        options={options.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
        onValueChange={(val) => {
          onChange(val);
        }}
      />
    </SettingsItem>
  );
};

/** 编码格式选项组件 */
export const CodecOption: FC<
  OptionPropsWithType<string> & {
    className?: string;
    codecOptions: Pick<CodecOptionsResult, 'defaultValue' | 'options'>;
  }
> = ({ value, onChange, onShouldUpgrade, className, codecOptions, type }) => {
  const userInfo = useStore((state) => state.userInfo);
  const modelVersion = useStore((state) => state[`${type}FormParams`].modelVersion);
  const { defaultValue, options } = codecOptions;
  const selectedValue = options.some((option) => option.value === value) ? (value ?? defaultValue) : defaultValue;

  return (
    <CommonSelect
      value={selectedValue}
      options={options}
      onValueChange={(val) => {
        if (!isOptionAllowed('codec', val, userInfo?.subs_plan, modelVersion)) {
          onShouldUpgrade?.('codec');
          return;
        }
        onChange(val);
      }}
      className={className}
    />
  );
};

/** 编码格式选项组件 */
export const CodecOptionSetting: FC<OptionPropsWithType<string>> = ({ value, onChange, onShouldUpgrade, type }) => {
  const t = useTranslations();
  const codecOptions = useCodecOptions();
  return (
    <SettingsItem
      title={t('create.createForm.settings.codec.title')}
      info={
        codecOptions.isHevcSupported
          ? t('create.createForm.settings.codec.tip')
          : t('create.createForm.settings.codec.tip264')
      }
    >
      <CodecOption
        value={value}
        onChange={(val) => onChange(val)}
        onShouldUpgrade={onShouldUpgrade}
        codecOptions={codecOptions}
        type={type}
      />
    </SettingsItem>
  );
};

/** 清晰度 & 编码格式 选项组件 */
export const ResolutionAndCodecOptionSetting: FC<OptionPropsWithType<{ resolution: string; codec: string }>> = ({
  value,
  onChange,
  onShouldUpgrade,
  type,
}) => {
  const t = useTranslations();
  const codecOptions = useCodecOptions();
  return (
    <SettingsItem
      title={
        <div className="flex flex-wrap items-center">
          <span>{t('create.createForm.settings.resolution.title')}</span>
          <span className="mx-1 text-system-white/40"> & </span>
          <span>{t('create.createForm.settings.codec.title')}</span>
        </div>
      }
      info={
        codecOptions.isHevcSupported
          ? t('create.createForm.settings.codec.tip')
          : t('create.createForm.settings.codec.tip264')
      }
    >
      <div className="flex items-center gap-2">
        <ResolutionOption
          value={value?.resolution}
          onChange={(val) => onChange({ resolution: val, codec: value?.codec ?? 'h265' })}
          onShouldUpgrade={onShouldUpgrade}
          type={type}
          showVipIcon={type !== 'reference2image'}
        />
        <CodecOption
          value={value?.codec}
          onChange={(val) => onChange({ resolution: value?.resolution ?? '720p', codec: val })}
          onShouldUpgrade={onShouldUpgrade}
          className="w-[106px] shrink-0 whitespace-nowrap px-1 tracking-tight"
          codecOptions={codecOptions}
          type={type}
        />
      </div>
    </SettingsItem>
  );
};
